import sum from 'lodash/sum';
import { useEffect, useMemo } from 'react';

import { QueryId, QueryParamUrlObject } from '@candu-ai/analytics/types';
import { ContentType } from '@candu-ai/content/types';
import {
  useHotspotMultiseries,
  useTourMultiseries,
} from 'dashboard/pages/Content/pages/Analytics/useTooltipMultiseriesApi';

import { aggregateSeries } from './aggregateSeries';
import { useGetAnalyticsQuery } from './useGetAnalyticsQuery';

interface Props {
  contentType: ContentType;
  queryParams: QueryParamUrlObject;
}

export const useGetTotalViews = ({ contentType, queryParams }: Props) => {
  const toursMultiseriesApi = useTourMultiseries(queryParams);
  const hotspotsMultiseriesApi = useHotspotMultiseries(queryParams);

  const queryId = useMemo(() => {
    if (contentType === ContentType.Tour) {
      return QueryId.TourStepViewsByNodeIdTimeseries;
    }

    if (contentType === ContentType.HotspotGroup) {
      return QueryId.HotspotBeaconViewsByNodeIdTimeseries;
    }

    return QueryId.ViewsTimeseries;
  }, [contentType]);

  const [viewsData, viewsAggregateData, , isLoadingTotalViews] = useGetAnalyticsQuery<any>({
    queryId,
    queryParams,
  });

  const multiseriesApi = useMemo(() => {
    if (contentType === ContentType.Tour) {
      return toursMultiseriesApi;
    }

    if (contentType === ContentType.HotspotGroup) {
      return hotspotsMultiseriesApi;
    }

    return undefined;
  }, [contentType, toursMultiseriesApi, hotspotsMultiseriesApi]);

  const totalViews: number = useMemo(() => {
    if (!viewsData?.metadata) {
      return 0;
    }

    if (multiseriesApi) {
      const entries = multiseriesApi.getEntries() || [];
      const sumData = aggregateSeries(viewsData);

      return sum(entries.map(({ value }) => (sumData[value] ?? 0) as number));
    }

    const [xAxis, yAxis] = viewsData.fields;
    // we need to grab the xAxis for metric (rather than timeseries) to work
    return sum(viewsData.rows.map((r) => r[yAxis?.name || xAxis?.name] as number));
  }, [viewsData, multiseriesApi]);

  useEffect(() => {
    if (!multiseriesApi || !multiseriesApi.hasLoaded() || !viewsData || !viewsAggregateData) {
      return;
    }
    multiseriesApi.onQueriesHaveLoaded(viewsData, viewsAggregateData);
  }, [multiseriesApi, viewsData, viewsAggregateData]);

  return {
    isLoading: isLoadingTotalViews || (multiseriesApi && !multiseriesApi.hasLoaded()),
    totalViews,
  };
};
